import React from "react";
import { FaExclamationTriangle, FaTrash, FaCheck, FaTimes } from "react-icons/fa";

/**
 * Dialog xác nhận với UI/UX đẹp
 * @param {boolean} isOpen - Trạng thái hiển thị dialog
 * @param {Function} onClose - Callback khi đóng dialog
 * @param {Function} onConfirm - Callback khi xác nhận
 * @param {string} title - Tiêu đề dialog
 * @param {string} message - Nội dung thông báo
 * @param {string} confirmText - Text của nút xác nhận (default: "Xác nhận")
 * @param {string} cancelText - Text của nút hủy (default: "Hủy")
 * @param {string} type - Loại dialog: "danger", "warning", "info", "success" (default: "warning")
 * @returns {JSX.Element} - Component ConfirmDialog
 */
const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Xác nhận",
  message = "Bạn có chắc chắn muốn thực hiện hành động này?",
  confirmText = "Xác nhận",
  cancelText = "Hủy",
  type = "warning",
}) => {
  if (!isOpen) return null;

  // Xác định icon và màu sắc theo loại dialog
  const getIconAndColor = () => {
    switch (type) {
      case "danger":
        return {
          icon: <FaTrash size={40} />,
          iconColor: "text-red-500",
          buttonColor: "bg-red-600 hover:bg-red-700",
        };
      case "warning":
        return {
          icon: <FaExclamationTriangle size={40} />,
          iconColor: "text-orange-500",
          buttonColor: "bg-orange-600 hover:bg-orange-700",
        };
      case "success":
        return {
          icon: <FaCheck size={40} />,
          iconColor: "text-green-500",
          buttonColor: "bg-green-600 hover:bg-green-700",
        };
      case "info":
      default:
        return {
          icon: <FaExclamationTriangle size={40} />,
          iconColor: "text-blue-500",
          buttonColor: "bg-blue-600 hover:bg-blue-700",
        };
    }
  };

  const { icon, iconColor, buttonColor } = getIconAndColor();

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  // Ngăn click vào overlay đóng dialog khi click vào content
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
      onClick={handleOverlayClick}
      style={{ zIndex: 9999 }}
    >
      <div
        className="bg-white rounded-lg shadow-2xl p-6 max-w-md w-full transform transition-all animate-fadeIn"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon */}
        <div className={`flex items-center justify-center mb-4 ${iconColor}`}>
          {icon}
        </div>

        {/* Title */}
        <h2 className="text-xl font-bold text-center mb-4 text-gray-900">
          {title}
        </h2>

        {/* Message */}
        <p className="mb-6 text-gray-700 text-center leading-relaxed">
          {message}
        </p>

        {/* Buttons */}
        <div className="flex justify-center space-x-4">
          <button
            onClick={handleCancel}
            className="px-6 py-2.5 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 font-medium transition-colors duration-200 flex items-center gap-2"
          >
            <FaTimes />
            {cancelText}
          </button>

          <button
            onClick={handleConfirm}
            className={`px-6 py-2.5 ${buttonColor} text-white rounded-md font-medium transition-colors duration-200 flex items-center gap-2 shadow-lg`}
          >
            <FaCheck />
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
